<script setup lang="ts">
/**
 * Label/badge: een afgeronde pill met kleur, voor statussen en kenmerken.
 *
 * De standaardlook (zacht: bg-*-100 + text-*-700, text-xs, px-2 py-0.5) is die van de
 * ±59 pills die pagina's met de hand bouwden; die gebruiken nu allemaal dit component.
 * De vorige VPill was een knopstijl zonder padding, met kleuren die deels niet werkten
 * (`secondary`/`info` = ongeldige CSS-variabelen, `default-gray` vs `default-grey`).
 *
 * Alle klassen staan voluit in de maps hieronder, zodat Tailwind ze vindt.
 */
import { computed } from 'vue'

export type PillColor =
    | 'gray'
    | 'blue'
    | 'green'
    | 'red'
    | 'amber'
    | 'yellow'
    | 'purple'
    | 'indigo'
    | 'orange'
    | 'primary'
    | 'success'
    | 'danger'
    | 'warning'

const props = withDefaults(
    defineProps<{
        color?: PillColor
        /** soft: lichte achtergrond (standaard); solid: volle kleur met witte tekst. */
        variant?: 'soft' | 'solid'
        size?: 'sm' | 'md'
        /** Gekleurd bolletje vóór de tekst. */
        dot?: boolean
    }>(),
    {
        color: 'gray',
        variant: 'soft',
        size: 'sm',
        dot: false,
    }
)

const SOFT: Record<PillColor, string> = {
    gray: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
    blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-200',
    green: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-200',
    red: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200',
    amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200',
    yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-200',
    purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-200',
    indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200',
    orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200',
    primary: 'bg-primary-10 text-primary',
    success: 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-200',
    danger: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-200',
    warning: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-200',
}

/**
 * Volle kleur. gray/primary/success/danger/warning volgen de whitelabel-themakleuren
 * (CSS-variabelen), zoals de vorige VPill: die plaatsen houden zo hun kleur.
 */
const SOLID: Record<PillColor, string> = {
    // Doorschijnend grijs: leesbaar op zowel witte als primary-achtergrond (tab-tellers).
    gray: 'bg-[color:var(--default-grey)] text-[color:var(--primary-text)]',
    blue: 'bg-blue-600 text-white',
    green: 'bg-green-600 text-white',
    red: 'bg-red-600 text-white',
    amber: 'bg-amber-500 text-white',
    yellow: 'bg-yellow-400 text-gray-900',
    purple: 'bg-purple-600 text-white',
    indigo: 'bg-indigo-600 text-white',
    orange: 'bg-orange-500 text-white',
    primary: 'bg-primary text-[color:var(--primary-text)]',
    success: 'bg-success text-[color:var(--success-text)]',
    danger: 'bg-danger text-[color:var(--danger-text)]',
    warning: 'bg-warning text-[color:var(--warning-text)]',
}

const DOT: Record<PillColor, string> = {
    gray: 'bg-gray-400',
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    red: 'bg-red-500',
    amber: 'bg-amber-500',
    yellow: 'bg-yellow-500',
    purple: 'bg-purple-500',
    indigo: 'bg-indigo-500',
    orange: 'bg-orange-500',
    primary: 'bg-primary',
    success: 'bg-green-500',
    danger: 'bg-red-500',
    warning: 'bg-amber-500',
}

const SIZE = {
    sm: 'px-2 py-0.5 text-xs gap-1',
    md: 'px-2.5 py-0.5 text-sm gap-1.5',
} as const

const classes = computed(() => [
    'v-pill inline-flex items-center rounded-full font-medium whitespace-nowrap',
    SIZE[props.size],
    props.variant === 'solid' ? SOLID[props.color] : SOFT[props.color],
])

const dotClasses = computed(() => [
    'inline-block w-1.5 h-1.5 rounded-full flex-shrink-0',
    props.variant === 'solid' ? 'bg-white' : DOT[props.color],
])
</script>

<template>
    <span :class="classes">
        <span v-if="dot" :class="dotClasses" aria-hidden="true"></span>
        <slot></slot>
    </span>
</template>
